import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActionButtonsSection = () => {
  const navigate = useNavigate();

  const primaryActions = [
    {
      id: 'upload',
      label: 'Upload New Bike',
      description: 'Get instant AI-powered upgrade recommendations',
      icon: 'Upload',
      variant: 'default',
      path: '/photo-upload',
      color: 'bg-accent text-accent-foreground'
    },
    {
      id: 'browse',
      label: 'Browse Recommendations',
      description: 'Explore all available upgrade suggestions',
      icon: 'Zap',
      variant: 'outline',
      path: '/upgrade-recommendations',
      color: 'bg-warning/10 text-warning'
    },
    {
      id: 'shops',
      label: 'Find Local Shops',
      description: 'Discover nearby bike stores and services',
      icon: 'MapPin',
      variant: 'outline',
      path: '/local-shop-finder',
      color: 'bg-success/10 text-success'
    }
  ];

  const secondaryActions = [
    {
      id: 'wishlist',
      label: 'My Wishlist',
      icon: 'Heart',
      path: '/wishlist',
      count: 12
    },
    {
      id: 'analysis',
      label: 'Analysis Results',
      icon: 'BarChart3',
      path: '/bike-analysis-results',
      count: null
    },
    {
      id: 'profile',
      label: 'Profile Settings',
      icon: 'Settings',
      path: '/settings',
      count: null
    }
  ];

  const handleActionClick = (path) => {
    navigate(path);
  };

  return (
    <div className="space-y-6">
      {/* Primary Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {primaryActions?.map((action) => (
            <div key={action?.id} className="bg-background border border-border rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start space-x-3 mb-4">
                <div className={`w-10 h-10 ${action?.color} rounded-lg flex items-center justify-center`}>
                  <Icon name={action?.icon} size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground mb-1">{action?.label}</h4>
                  <p className="text-sm text-muted-foreground">{action?.description}</p>
                </div>
              </div>
              
              <Button
                variant={action?.variant}
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => handleActionClick(action?.path)}
              >
                {action?.label}
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Secondary Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">More Options</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {secondaryActions?.map((action) => (
            <button
              key={action?.id}
              onClick={() => handleActionClick(action?.path)}
              className="flex items-center justify-between p-3 bg-background border border-border rounded-lg hover:bg-muted transition-colors interactive-scale"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={action?.icon} size={16} color="var(--color-accent)" />
                </div>
                <span className="text-sm font-medium text-foreground">{action?.label}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                {action?.count && (
                  <span className="bg-accent/10 text-accent text-xs px-2 py-1 rounded-full">
                    {action?.count}
                  </span>
                )}
                <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Help & Support */}
      <div className="bg-gradient-to-r from-accent/5 to-accent/10 border border-accent/20 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
            <Icon name="HelpCircle" size={24} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Need Help Getting Started?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Learn how to get the most out of BikeUpgrade Pro with our comprehensive guides and tutorials.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="BookOpen"
                iconPosition="left"
                onClick={() => window.open('/help/getting-started', '_blank')}
              >
                Getting Started
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={() => window.open('/support', '_blank')}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionButtonsSection;